import { useCallback, useEffect, useState } from "react";
import {
  detectInstalledRelatedWebApp,
  getDeferredInstallPrompt,
  initPwaInstallCapture,
  isChromiumInstallBrowser,
  isIosDevice,
  isRunningAsInstalledPwa,
  promptNativeInstall,
  subscribeInstallPrompt,
  supportsNativeInstallPrompt,
} from "../pwa/installPrompt";
import { ensureSmartTTServiceWorkerReady } from "../pwa/registerServiceWorker";

/**
 * @returns {{
 *   installed: boolean,
 *   canPrompt: boolean,
 *   isIos: boolean,
 *   supportsNativePrompt: boolean,
 *   isChromium: boolean,
 *   install: () => Promise<"accepted"|"dismissed"|"unavailable"|"installed"|"preparing"|"menu">,
 * }}
 */
export function usePwaInstall() {
  const [installed, setInstalled] = useState(() => isRunningAsInstalledPwa());
  const [canPrompt, setCanPrompt] = useState(() => !!getDeferredInstallPrompt());
  const [isIos, setIsIos] = useState(() => isIosDevice());
  const [isChromium] = useState(() => isChromiumInstallBrowser());
  const [supportsNativePrompt] = useState(() => supportsNativeInstallPrompt());

  useEffect(() => {
    initPwaInstallCapture();
    setIsIos(isIosDevice());
    setInstalled(isRunningAsInstalledPwa());
    ensureSmartTTServiceWorkerReady().catch(() => undefined);
    detectInstalledRelatedWebApp().then((yes) => {
      if (yes) setInstalled(true);
    });

    return subscribeInstallPrompt((prompt) => {
      setCanPrompt(!!prompt);
      setInstalled(isRunningAsInstalledPwa());
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setInstalled(isRunningAsInstalledPwa());
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const install = useCallback(async () => {
    if (isRunningAsInstalledPwa()) {
      setInstalled(true);
      return "installed";
    }

    // Must call prompt() in the same user-gesture turn — no awaits before this.
    if (getDeferredInstallPrompt()) {
      const { outcome } = await promptNativeInstall();
      setCanPrompt(!!getDeferredInstallPrompt());
      if (outcome === "accepted") {
        setInstalled(true);
        return "accepted";
      }
      if (outcome === "dismissed") return "dismissed";
      return "unavailable";
    }

    ensureSmartTTServiceWorkerReady().catch(() => undefined);
    return "preparing";
  }, []);

  return {
    installed,
    canPrompt,
    isIos,
    isChromium,
    supportsNativePrompt,
    install,
  };
}
